import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "@/components/ui/hover-card";
import {
  ChevronLeft, ChevronRight, CalendarDays, Plus, Filter, X,
  Stethoscope, Hospital as HospitalIcon, Smile, Eye, Activity, ClipboardList,
  Phone, MoreHorizontal, UserRound, MapPin, Clock, CheckCircle2, XCircle,
  AlertTriangle, CircleDot, PanelRightOpen, PanelRightClose, ChevronRight as ChevRight,
  FileDown,
} from "lucide-react";
import CareFlagsPills, { CARE_FLAG_META, CARE_FLAG_ORDER } from "@/components/CareFlagsPills";
import MobileAppointmentsAgenda from "@/components/mobile/MobileAppointmentsAgenda";

/* ---------- Type + Status colour + icon system ---------- */

// Soft background · dark text · richer left-border colour so meaning survives
// even for colour-blind users (icon + label are always present).
const TYPE_META = {
  GP:        { label: "GP",         Icon: Stethoscope,   soft: "bg-blue-50",    text: "text-blue-800",    border: "border-blue-500"    },
  Consultant:{ label: "Consultant", Icon: ClipboardList, soft: "bg-violet-50",  text: "text-violet-800",  border: "border-violet-500"  },
  Dentist:   { label: "Dentist",    Icon: Smile,         soft: "bg-cyan-50",    text: "text-cyan-800",    border: "border-cyan-500"    },
  Optician:  { label: "Optician",   Icon: Eye,           soft: "bg-sky-50",     text: "text-sky-800",     border: "border-sky-500"     },
  Physio:    { label: "Physio",     Icon: Activity,      soft: "bg-teal-50",    text: "text-teal-800",    border: "border-teal-500"    },
  Review:    { label: "Review",     Icon: ClipboardList, soft: "bg-indigo-50",  text: "text-indigo-800",  border: "border-indigo-500"  },
  Hospital:  { label: "Hospital",   Icon: HospitalIcon,  soft: "bg-purple-50",  text: "text-purple-800",  border: "border-purple-500"  },
  Telephone: { label: "Telephone",  Icon: Phone,         soft: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-500"   },
  Other:     { label: "Other",      Icon: MoreHorizontal,soft: "bg-slate-50",   text: "text-slate-800",   border: "border-slate-400"   },
};
const TYPES = Object.keys(TYPE_META);
const typeMeta = (t) => TYPE_META[t] || TYPE_META.Other;

const STATUS_META = {
  scheduled:  { label: "Scheduled",  Icon: CircleDot,      pill: "bg-slate-100 text-slate-700 border-slate-300"    },
  confirmed:  { label: "Confirmed",  Icon: CheckCircle2,   pill: "bg-emerald-50 text-emerald-800 border-emerald-300"},
  completed:  { label: "Completed",  Icon: CheckCircle2,   pill: "bg-green-100 text-green-800 border-green-300"    },
  cancelled:  { label: "Cancelled",  Icon: XCircle,        pill: "bg-red-50 text-red-700 border-red-300"           },
  no_show:    { label: "Missed",     Icon: AlertTriangle,  pill: "bg-red-100 text-red-800 border-red-300"          },
  rescheduled:{ label: "Rescheduled",Icon: CircleDot,      pill: "bg-amber-50 text-amber-800 border-amber-300"     },
  needs_attention:{ label: "Needs attention", Icon: AlertTriangle, pill: "bg-amber-100 text-amber-900 border-amber-400" },
};
const STATUSES = Object.keys(STATUS_META);
const statusMeta = (s) => STATUS_META[s] || STATUS_META.scheduled;

/* ---------- Utilities ---------- */

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startWeekday);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return (iso || "").slice(11, 16); }
}

function endTime(iso, mins) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (mins) d.setMinutes(d.getMinutes() + Number(mins));
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

// Safe display for the service-user name. The backend now returns
// "Unknown Service User" when the join misses, but this guards against
// any legacy record that still carries a UUID or empty value.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function safeClientName(a) {
  const n = (a?.client_name || "").trim();
  if (!n || UUID_RE.test(n)) return "Unknown Service User";
  return n;
}

/* ---------- Sub-components ---------- */

function SummaryCard({ label, value, tone = "slate", active, onClick, testid }) {
  const tones = {
    slate:   "bg-white border-slate-200 hover:border-slate-400",
    sky:     "bg-sky-50/60 border-sky-200 hover:border-sky-400",
    purple:  "bg-purple-50/60 border-purple-200 hover:border-purple-400",
    amber:   "bg-amber-50/60 border-amber-200 hover:border-amber-400",
    red:     "bg-red-50/60 border-red-200 hover:border-red-400",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left px-3 py-2 border transition-colors ${tones[tone] || tones.slate} ${active ? "ring-2 ring-offset-1 ring-slate-800" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums leading-tight mt-0.5">{value}</div>
    </button>
  );
}

function AppointmentPreview({ a, safety, onEdit, onView, onOpenDrawer }) {
  const t = typeMeta(a.appointment_type);
  const s = statusMeta(a.status);
  const flags = safety?.[a.client_id]?.care_flags || [];
  const SIcon = s.Icon;
  const TIcon = t.Icon;
  return (
    <div className="text-left w-72" data-testid={`appt-preview-${a.id}`}>
      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 ${t.soft} ${t.text} ${t.border}`}>
        <TIcon className="w-3 h-3" /> {t.label}
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-2">{a.title || t.label + " Appointment"}</div>
      <div className="text-xs text-slate-600 mt-2 space-y-1">
        <div className="flex items-start gap-2"><UserRound className="w-3.5 h-3.5 mt-0.5 text-slate-400" /><span className="font-medium">{safeClientName(a)}</span></div>
        <div className="flex items-start gap-2"><Clock className="w-3.5 h-3.5 mt-0.5 text-slate-400" /><span>{fmtTime(a.scheduled_at)}{a.duration_min ? ` – ${endTime(a.scheduled_at, a.duration_min)}` : ""}</span></div>
        {a.location && <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400" /><span>{a.location}</span></div>}
        {a.clinician && <div className="flex items-start gap-2"><Stethoscope className="w-3.5 h-3.5 mt-0.5 text-slate-400" /><span>{a.clinician}</span></div>}
        {a.escort_staff && <div className="flex items-start gap-2"><UserRound className="w-3.5 h-3.5 mt-0.5 text-slate-400" /><span>{a.escort_staff} · Escort</span></div>}
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 ${s.pill}`}>
          <SIcon className="w-3 h-3" /> {s.label}
        </span>
        {flags.length > 0 && <CareFlagsPills flags={flags} size="sm" />}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={onOpenDrawer} data-testid={`preview-view-${a.id}`}>View</Button>
        <Button size="sm" className="h-7 text-xs flex-1 bg-slate-900 hover:bg-slate-800" onClick={onEdit} data-testid={`preview-edit-${a.id}`}>Edit</Button>
      </div>
    </div>
  );
}

function AppointmentChip({ a, safety, compact, onOpenDrawer, onEdit }) {
  const t = typeMeta(a.appointment_type);
  const s = statusMeta(a.status);
  const TIcon = t.Icon;
  const isCancelled = a.status === "cancelled";
  const flags = safety?.[a.client_id]?.care_flags || [];
  return (
    <HoverCard openDelay={140} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          onClick={onOpenDrawer}
          data-testid={`cal-appt-${a.id}`}
          className={`w-full block text-left border-l-4 ${t.border} ${t.soft} ${t.text} hover:brightness-95 transition-all ${isCancelled ? "opacity-60" : ""}`}
          title={`${t.label} · ${safeClientName(a)}${a.location ? " · " + a.location : ""}`}
        >
          {compact ? (
            <div className="px-1.5 py-0.5 text-[10px] flex items-center gap-1 truncate">
              <TIcon className="w-3 h-3 shrink-0" />
              <span className="tabular-nums font-semibold">{fmtTime(a.scheduled_at)}</span>
              <span className={`truncate ${isCancelled ? "line-through" : ""}`}>{safeClientName(a).split(" ")[0]}</span>
              {a.status === "completed" && <CheckCircle2 className="w-2.5 h-2.5 text-green-700 shrink-0" />}
              {a.status === "no_show" && <AlertTriangle className="w-2.5 h-2.5 text-red-700 shrink-0" />}
            </div>
          ) : (
            <div className="px-1.5 py-1 text-[10px] leading-tight space-y-0.5">
              <div className="flex items-center gap-1 font-semibold">
                <TIcon className="w-3 h-3 shrink-0" />
                <span className="truncate">{t.label} — {safeClientName(a).split(" ")[0]}</span>
                {a.status === "completed" && <CheckCircle2 className="w-2.5 h-2.5 text-green-700 shrink-0 ml-auto" />}
                {a.status === "no_show" && <AlertTriangle className="w-2.5 h-2.5 text-red-700 shrink-0 ml-auto" />}
              </div>
              <div className={`text-[9px] tabular-nums opacity-80 truncate ${isCancelled ? "line-through" : ""}`}>
                {fmtTime(a.scheduled_at)}{a.location ? ` · ${a.location}` : ""}
              </div>
              {a.escort_staff && (
                <div className="text-[9px] opacity-80 truncate flex items-center gap-0.5">
                  <UserRound className="w-2.5 h-2.5" /> {a.escort_staff.split(" ")[0]} · Escort
                </div>
              )}
              {flags.length > 0 && (
                <div className="flex items-center gap-0.5">
                  {flags.slice(0, 3).map((k) => {
                    const m = CARE_FLAG_META[k];
                    const FI = m?.Icon;
                    return FI ? <FI key={k} className="w-2.5 h-2.5 text-red-700" strokeWidth={2.25} title={m.label} /> : null;
                  })}
                </div>
              )}
            </div>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 p-3">
        <AppointmentPreview a={a} safety={safety} onEdit={onEdit} onView={onOpenDrawer} onOpenDrawer={onOpenDrawer} />
      </HoverCardContent>
    </HoverCard>
  );
}

function UpcomingPanel({ items, safety, onOpen, open, onOpenDrawer }) {
  const groups = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const endWeek = new Date(today); endWeek.setDate(endWeek.getDate() + 7);
    const g = { Today: [], Tomorrow: [], "This Week": [] };
    items
      .filter((a) => a.status !== "cancelled" && a.status !== "completed")
      .forEach((a) => {
        const dt = new Date(a.scheduled_at);
        if (isNaN(dt)) return;
        if (dt < today) return;
        if (dt < tomorrow) g.Today.push(a);
        else if (dt < new Date(tomorrow.getTime() + 86400000)) g.Tomorrow.push(a);
        else if (dt < endWeek) g["This Week"].push(a);
      });
    Object.values(g).forEach((arr) => arr.sort((x,y)=>(x.scheduled_at||"").localeCompare(y.scheduled_at||"")));
    return g;
  }, [items]);
  if (!open) {
    return (
      <button
        onClick={() => onOpen(true)}
        className="hidden xl:flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 bg-white px-2 py-1"
        data-testid="btn-open-upcoming"
      >
        <PanelRightOpen className="w-3.5 h-3.5" /> Upcoming
      </button>
    );
  }
  return (
    <aside className="hidden xl:flex flex-col w-72 shrink-0 border border-slate-200 bg-white" data-testid="upcoming-panel">
      <div className="h-10 border-b border-slate-200 px-3 flex items-center justify-between bg-slate-50/70">
        <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold">Upcoming</div>
        <button onClick={() => onOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="btn-close-upcoming"><PanelRightClose className="w-4 h-4" /></button>
      </div>
      <div className="overflow-y-auto p-2 space-y-3 flex-1">
        {Object.entries(groups).map(([label, arr]) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1 pb-1">{label}</div>
            {arr.length === 0 ? (
              <div className="text-xs text-slate-400 px-1 py-1">Nothing scheduled.</div>
            ) : (
              <ul className="space-y-1">
                {arr.map((a) => {
                  const t = typeMeta(a.appointment_type);
                  const TIcon = t.Icon;
                  return (
                    <li key={a.id}>
                      <button
                        onClick={() => onOpenDrawer(a)}
                        data-testid={`upcoming-${a.id}`}
                        className={`w-full text-left border-l-4 ${t.border} ${t.soft} hover:brightness-95 px-2 py-1.5`}
                      >
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-900">
                          <TIcon className={`w-3 h-3 ${t.text}`} />
                          <span className="tabular-nums">{fmtTime(a.scheduled_at)}</span>
                          <span className="truncate">· {safeClientName(a)}</span>
                        </div>
                        <div className={`text-[10px] ${t.text} truncate mt-0.5`}>{t.label}{a.location ? ` · ${a.location}` : ""}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}

/* ---------- Main Calendar page ---------- */

export default function Calendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [drawerAppt, setDrawerAppt] = useState(null);
  const [filterClient, setFilterClient] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEscort, setFilterEscort] = useState("");
  const [filterFlags, setFilterFlags] = useState([]);
  const [filterOnlyEscort, setFilterOnlyEscort] = useState(false);
  const [filterOnlyToday, setFilterOnlyToday] = useState(false);
  const [safety, setSafety] = useState({});
  const [upcomingOpen, setUpcomingOpen] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [addForm, setAddForm] = useState({ client_id: "", title: "", appointment_type: "GP", scheduled_at: "", duration_min: 30, location: "", clinician: "", escort_staff: "", notes: "", status: "scheduled" });
  const [saving, setSaving] = useState(false);
  // Batch 5.2 · mobile filter bottom-sheet state. Zero effect on desktop.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const monthStart = new Date(year, month, 1).toISOString().slice(0, 10);
  const monthEnd = new Date(year, month + 1, 0).toISOString().slice(0, 10);
  const todayIso = today.toISOString().slice(0, 10);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/dashboard/appointments-range?start=${monthStart}&end=${monthEnd}`),
      api.get("/clients"),
      api.get("/dashboard/client-safety").catch(() => ({ data: { clients: {} } })),
    ]).then(([r, c, sf]) => { setItems(r.data?.items || []); setClients(c.data || []); setSafety(sf.data?.clients || {}); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [monthStart, monthEnd]);

  const escorts = useMemo(
    () => Array.from(new Set(items.map((i) => i.escort_staff).filter(Boolean))).sort(),
    [items],
  );

  const filtered = useMemo(() => items.filter((a) => {
    if (filterClient !== "all" && a.client_id !== filterClient) return false;
    if (filterType !== "all" && a.appointment_type !== filterType) return false;
    if (filterStatus !== "all" && (a.status || "scheduled") !== filterStatus) return false;
    if (filterEscort.trim() && !(a.escort_staff || "").toLowerCase().includes(filterEscort.trim().toLowerCase())) return false;
    if (filterFlags.length > 0 && !filterFlags.every((fl) => (safety[a.client_id]?.care_flags || []).includes(fl))) return false;
    if (filterOnlyEscort && !a.escort_staff) return false;
    if (filterOnlyToday && (a.scheduled_at || "").slice(0, 10) !== todayIso) return false;
    return true;
  }), [items, filterClient, filterType, filterStatus, filterEscort, filterFlags, filterOnlyEscort, filterOnlyToday, safety, todayIso]);

  const days = useMemo(() => monthGrid(year, month), [year, month]);
  const byDay = useMemo(() => {
    const m = {};
    filtered.forEach((a) => {
      const day = (a.scheduled_at || "").slice(0, 10);
      (m[day] ||= []).push(a);
    });
    Object.values(m).forEach((arr) => arr.sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || "")));
    return m;
  }, [filtered]);

  // Summary counts respect current filters (except the pill they represent).
  const summary = useMemo(() => {
    const todayCount = filtered.filter((a) => (a.scheduled_at || "").slice(0,10) === todayIso).length;
    const hospital = filtered.filter((a) => a.appointment_type === "Hospital").length;
    const escort = filtered.filter((a) => !!a.escort_staff).length;
    const attn = filtered.filter((a) => ["no_show", "needs_attention"].includes(a.status)).length;
    return { total: filtered.length, today: todayCount, hospital, escort, attn };
  }, [filtered, todayIso]);

  const dayList = detail ? (byDay[detail] || []) : [];
  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const filterCount =
    (filterClient !== "all" ? 1 : 0) +
    (filterType !== "all" ? 1 : 0) +
    (filterStatus !== "all" ? 1 : 0) +
    (filterEscort.trim() ? 1 : 0) +
    filterFlags.length +
    (filterOnlyEscort ? 1 : 0) +
    (filterOnlyToday ? 1 : 0);

  const shift = (n) => {
    let m = month + n, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setDetail(null);
  };
  const clearFilters = () => {
    setFilterClient("all"); setFilterType("all"); setFilterStatus("all");
    setFilterEscort(""); setFilterFlags([]); setFilterOnlyEscort(false); setFilterOnlyToday(false);
  };
  const toggleFlag = (k) => setFilterFlags((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const openAddForDay = (iso) => {
    const defaultStart = `${iso}T09:00`;
    setEditingId(null);
    setAddForm({ client_id: clients[0]?.id || "", title: "", appointment_type: "GP", scheduled_at: defaultStart, duration_min: 30, location: "", clinician: "", escort_staff: filterEscort || "", notes: "", status: "scheduled" });
    setAddOpen(true);
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setAddForm({
      client_id: a.client_id || "",
      title: a.title || "",
      appointment_type: a.appointment_type || "GP",
      scheduled_at: (a.scheduled_at || "").slice(0, 16),
      duration_min: a.duration_min || 30,
      location: a.location || "",
      clinician: a.clinician || "",
      escort_staff: a.escort_staff || "",
      notes: a.notes || "",
      status: a.status || "scheduled",
    });
    setAddOpen(true);
    setDrawerAppt(null);
  };

  const submitAdd = async () => {
    if (!addForm.client_id) { toast.error("Pick a service user"); return; }
    if (!addForm.title.trim()) { toast.error("Title is required"); return; }
    if (!addForm.scheduled_at) { toast.error("Date & time is required"); return; }
    setSaving(true);
    try {
      const payload = {
        data: {
          title: addForm.title.trim(),
          appointment_type: addForm.appointment_type,
          scheduled_at: addForm.scheduled_at,
          duration_min: addForm.duration_min ? Number(addForm.duration_min) : null,
          location: addForm.location.trim() || null,
          clinician: addForm.clinician.trim() || null,
          escort_staff: addForm.escort_staff.trim() || null,
          notes: addForm.notes.trim() || null,
          status: addForm.status || "scheduled",
        },
      };
      if (editingId) {
        await api.put(`/clients/${addForm.client_id}/sections/appointments/${editingId}`, payload);
        toast.success("Appointment updated");
      } else {
        await api.post(`/clients/${addForm.client_id}/sections/appointments`, payload);
        toast.success("Appointment created");
      }
      setAddOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const cancelAppt = async () => {
    if (!editingId) return;
    if (!window.confirm("Mark this appointment as cancelled?")) return;
    setSaving(true);
    try {
      await api.put(`/clients/${addForm.client_id}/sections/appointments/${editingId}`, { data: { ...addForm, status: "cancelled", duration_min: addForm.duration_min ? Number(addForm.duration_min) : null } });
      toast.success("Appointment cancelled");
      setAddOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const deleteAppt = async () => {
    if (!editingId) return;
    if (!window.confirm("Delete this appointment for good? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.delete(`/clients/${addForm.client_id}/sections/appointments/${editingId}`);
      toast.success("Appointment deleted");
      setAddOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Quick drawer actions — patch status via the same PUT endpoint.
  const quickStatus = async (a, status, msg) => {
    try {
      await api.put(`/clients/${a.client_id}/sections/appointments/${a.id}`, {
        data: {
          title: a.title, appointment_type: a.appointment_type, scheduled_at: a.scheduled_at,
          duration_min: a.duration_min || null, location: a.location || null, clinician: a.clinician || null,
          escort_staff: a.escort_staff || null, notes: a.notes || null, status,
        },
      });
      toast.success(msg);
      setDrawerAppt(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50/40">
      {/* -----------------------------------------------------------------
          Batch 5.2 · MOBILE / TABLET view ( <lg ) — Today / Upcoming
          agenda. Reuses the SAME state, filters, summary, drawer and add
          dialog as desktop. Card tap opens the existing drawer. No
          mobile mutation surface, no new API, no new status.
          --------------------------------------------------------------- */}
      <MobileAppointmentsAgenda
        loading={loading}
        items={items}
        filtered={filtered}
        summary={summary}
        safety={safety}
        typeMeta={typeMeta}
        statusMeta={statusMeta}
        monthLabel={monthLabel}
        filterCount={filterCount}
        filterOnlyToday={filterOnlyToday}
        filterOnlyEscort={filterOnlyEscort}
        filterStatus={filterStatus}
        filterType={filterType}
        onShift={shift}
        onGoToday={() => { const t = new Date(); setYear(t.getFullYear()); setMonth(t.getMonth()); setDetail(null); }}
        onOpenAdd={() => openAddForDay(todayIso)}
        onOpenDrawer={(a) => setDrawerAppt(a)}
        onOpenFilters={() => setMobileFiltersOpen(true)}
        onRefresh={load}
        onClearFilters={clearFilters}
        onToggleToday={() => setFilterOnlyToday((v) => !v)}
        onToggleEscort={() => setFilterOnlyEscort((v) => !v)}
        onToggleHospital={() => setFilterType(filterType === "Hospital" ? "all" : "Hospital")}
        onToggleAttn={() => setFilterStatus(filterStatus === "no_show" ? "all" : "no_show")}
      />

      {/* Batch 5.2 · Mobile filters bottom sheet. Uses the same filter
          state as desktop so applied filters carry across viewports. */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="bottom" className="p-0 max-h-[90dvh] rounded-t-2xl border-slate-200 bg-white flex flex-col" data-testid="mobile-appt-filter-sheet">
          <SheetHeader className="shrink-0 px-5 pt-5 pb-3 border-b border-slate-100 flex-row items-center justify-between space-y-0">
            <SheetTitle className="text-base">Filter appointments</SheetTitle>
            <button onClick={() => setMobileFiltersOpen(false)} className="w-11 h-11 -mr-2 inline-flex items-center justify-center rounded-md hover:bg-slate-100" aria-label="Close" data-testid="mobile-appt-filter-close">
              <X className="w-4 h-4" />
            </button>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Service user</span>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="h-11 mt-1" data-testid="mobile-filter-client"><SelectValue placeholder="All service users" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All service users</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Type</span>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-11 mt-1" data-testid="mobile-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Status</span>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-11 mt-1" data-testid="mobile-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Escort staff</span>
              <Input placeholder="Type escort name" value={filterEscort} onChange={(e) => setFilterEscort(e.target.value)} data-testid="mobile-filter-escort" className="h-11 mt-1" />
            </label>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Safety flags</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="mobile-filter-flags">
                {CARE_FLAG_ORDER.map((k) => {
                  const m = CARE_FLAG_META[k];
                  const on = filterFlags.includes(k);
                  const Icon = m.Icon;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleFlag(k)}
                      data-testid={`mobile-filter-flag-${k}`}
                      className={`min-h-[44px] inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-2 py-1 rounded ${on ? m.color : "bg-white text-slate-400 border-slate-200"}`}
                    >
                      <Icon className="w-3 h-3" strokeWidth={2.25} /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 min-h-[44px]">
              <input type="checkbox" checked={filterOnlyEscort} onChange={(e) => setFilterOnlyEscort(e.target.checked)} data-testid="mobile-filter-only-escort" className="w-4 h-4" />
              <span className="text-xs">Only appointments needing an escort</span>
            </label>
            <label className="flex items-center gap-2 min-h-[44px]">
              <input type="checkbox" checked={filterOnlyToday} onChange={(e) => setFilterOnlyToday(e.target.checked)} data-testid="mobile-filter-only-today" className="w-4 h-4" />
              <span className="text-xs">Only today</span>
            </label>
          </div>
          <div className="shrink-0 border-t border-slate-100 px-5 py-3 flex items-center gap-2 bg-white" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
            <Button variant="outline" onClick={clearFilters} data-testid="mobile-appt-filter-clear" className="h-11 flex-1">Clear</Button>
            <Button onClick={() => setMobileFiltersOpen(false)} data-testid="mobile-appt-filter-apply" className="h-11 flex-1 bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Header — desktop only from Batch 5.2 */}
      <div className="hidden lg:flex border-b border-slate-200 bg-white px-6 py-4 items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Care Delivery</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-heading text-3xl font-semibold text-slate-900" data-testid="calendar-title">{monthLabel}</h1>
            <span className="text-sm text-slate-500">Appointments Calendar</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="h-9 w-9 border border-slate-300 hover:bg-slate-50 inline-flex items-center justify-center rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
          <Button size="sm" variant="outline" onClick={() => { const t = new Date(); setYear(t.getFullYear()); setMonth(t.getMonth()); setDetail(null); }} data-testid="cal-today" className="h-9">
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Today
          </Button>
          <button onClick={() => shift(1)} data-testid="cal-next" className="h-9 w-9 border border-slate-300 hover:bg-slate-50 inline-flex items-center justify-center rounded-sm"><ChevronRight className="w-4 h-4" /></button>
          <Button size="sm" onClick={() => openAddForDay(todayIso)} data-testid="btn-cal-new" className="h-9 bg-slate-900 hover:bg-slate-800">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New appointment
          </Button>
        </div>
      </div>

      {/* Filters — desktop only from Batch 5.2 */}
      <div className="hidden lg:block px-6 pt-4">
        <div className="flex items-center gap-2 flex-wrap" data-testid="cal-filters">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            <Filter className="w-3 h-3" /> Filters
          </div>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="h-8 w-56" data-testid="filter-client"><SelectValue placeholder="All service users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All service users</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 w-40" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-40" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Escort staff…" value={filterEscort} onChange={(e) => setFilterEscort(e.target.value)} list="escort-list" data-testid="filter-escort" className="h-8 w-40" />
          <datalist id="escort-list">
            {escorts.map((e) => <option key={e} value={e} />)}
          </datalist>
          <div className="flex items-center gap-1 flex-wrap" data-testid="filter-flags">
            {CARE_FLAG_ORDER.map((k) => {
              const m = CARE_FLAG_META[k];
              const on = filterFlags.includes(k);
              const Icon = m.Icon;
              return (
                <button
                  key={k}
                  onClick={() => toggleFlag(k)}
                  data-testid={`filter-flag-${k}`}
                  title={m.full || m.label}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-1 ${on ? m.color : "bg-white text-slate-400 border-slate-200 hover:text-slate-700"}`}
                >
                  <Icon className="w-3 h-3" strokeWidth={2.25} />
                  {m.label}
                </button>
              );
            })}
          </div>
          {filterCount > 0 && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="btn-clear-filters" className="h-8 text-xs">
              <X className="w-3.5 h-3.5 mr-1" /> Clear {filterCount} filter{filterCount !== 1 ? "s" : ""}
            </Button>
          )}
          <div className="ml-auto text-[11px] text-slate-500" data-testid="filter-count">{filtered.length} appointment{filtered.length !== 1 ? "s" : ""} shown</div>
        </div>
      </div>

      {/* Summary strip — desktop only from Batch 5.2 */}
      <div className="hidden lg:block px-6 pt-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="cal-summary-strip">
          <SummaryCard label="Appointments" value={summary.total} tone="slate" testid="summary-total" onClick={clearFilters} />
          <SummaryCard label="Today" value={summary.today} tone="sky" active={filterOnlyToday} testid="summary-today" onClick={() => setFilterOnlyToday((v) => !v)} />
          <SummaryCard label="Hospital" value={summary.hospital} tone="purple" active={filterType === "Hospital"} testid="summary-hospital" onClick={() => setFilterType(filterType === "Hospital" ? "all" : "Hospital")} />
          <SummaryCard label="Escort required" value={summary.escort} tone="amber" active={filterOnlyEscort} testid="summary-escort" onClick={() => setFilterOnlyEscort((v) => !v)} />
          <SummaryCard label="Needs attention" value={summary.attn} tone="red" active={filterStatus === "no_show"} testid="summary-attn" onClick={() => setFilterStatus(filterStatus === "no_show" ? "all" : "no_show")} />
        </div>
      </div>

      {/* Calendar body + Upcoming — desktop only from Batch 5.2 */}
      <div className="hidden lg:flex p-6 gap-4">
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="text-sm text-slate-500" data-testid="cal-loading">Loading month…</div>
          ) : (
            <>
              <div className="grid grid-cols-7 border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="cal-grid">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <div key={d} className={`px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold border-b border-slate-200 ${i >= 5 ? "bg-slate-100 text-slate-600" : "bg-slate-50 text-slate-500"}`}>{d}</div>
                ))}
                {days.map((d, idx) => {
                  const iso = d.toISOString().slice(0, 10);
                  const inMonth = d.getMonth() === month;
                  const isToday = iso === todayIso;
                  const isSelected = detail === iso;
                  const dow = (d.getDay() + 6) % 7;
                  const isWeekend = dow >= 5;
                  const appts = byDay[iso] || [];
                  const clash = appts.length >= 3;
                  return (
                    <div
                      key={iso + "-" + idx}
                      data-testid={`cal-day-${iso}`}
                      className={`min-h-[110px] border-b border-r border-slate-100 p-1.5 flex flex-col group relative transition-colors
                        ${inMonth ? (isWeekend ? "bg-slate-50/60" : "bg-white") : "bg-slate-50 text-slate-400"}
                        ${isToday ? "bg-sky-50/70" : ""}
                        ${isSelected ? "ring-2 ring-slate-900 ring-inset z-[1]" : ""}
                      `}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <button
                          onClick={() => setDetail(isSelected ? null : iso)}
                          className={`inline-flex items-center gap-1.5 text-xs tabular-nums text-left ${isToday ? "font-semibold text-sky-900" : "text-slate-700"} hover:text-slate-900`}
                          data-testid={`cal-day-btn-${iso}`}
                        >
                          <span className={isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-sky-600 text-white text-[11px] font-semibold" : ""}>{d.getDate()}</span>
                          {isToday && <span className="text-[9px] uppercase tracking-widest text-sky-700 font-semibold">Today</span>}
                        </button>
                        <div className="flex items-center gap-1">
                          {clash && <span title="3+ appointments — check for clashes" className="text-[9px] uppercase tracking-widest text-red-700 font-semibold" data-testid={`cal-clash-${iso}`}>clash</span>}
                          <button
                            onClick={() => openAddForDay(iso)}
                            data-testid={`cal-add-${iso}`}
                            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-700 transition-opacity"
                            title="Add appointment on this day"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1 flex-1">
                        {appts.slice(0, 3).map((a) => (
                          <AppointmentChip
                            key={a.id}
                            a={a}
                            safety={safety}
                            compact={appts.length >= 3}
                            onOpenDrawer={() => setDrawerAppt(a)}
                            onEdit={() => openEdit(a)}
                          />
                        ))}
                        {appts.length > 3 && (
                          <button onClick={() => setDetail(iso)} className="text-[10px] text-slate-500 hover:text-slate-800 hover:underline" data-testid={`cal-more-${iso}`}>
                            + {appts.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {detail && (
                <div className="mt-5 bg-white border border-slate-200 p-4 rounded-sm" data-testid="cal-detail-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Detail · {new Date(detail).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                      <div className="text-sm text-slate-700">{dayList.length} appointment{dayList.length !== 1 ? "s" : ""} scheduled</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openAddForDay(detail)} data-testid="btn-detail-add" className="h-8">
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
                      </Button>
                      <button onClick={() => setDetail(null)} className="text-xs text-slate-500 hover:text-slate-800" data-testid="cal-detail-close">Close</button>
                    </div>
                  </div>
                  {dayList.length === 0 ? (
                    <div className="text-xs text-slate-500 py-4">Nothing scheduled for this day.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100" data-testid="cal-detail-list">
                      {dayList.map((a) => {
                        const t = typeMeta(a.appointment_type);
                        const s = statusMeta(a.status);
                        const TIcon = t.Icon; const SIcon = s.Icon;
                        return (
                          <li key={a.id} className="py-2 flex items-start gap-3" data-testid={`cal-detail-${a.id}`}>
                            <div className="text-[11px] font-semibold text-slate-700 shrink-0 w-14 tabular-nums">{fmtTime(a.scheduled_at)}</div>
                            <div className={`w-1 self-stretch ${t.border.replace("border-", "bg-")} shrink-0`} />
                            <TIcon className={`w-4 h-4 ${t.text} shrink-0 mt-0.5`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                                <button onClick={() => setDrawerAppt(a)} className="hover:text-slate-700 hover:underline text-left" data-testid={`btn-open-appt-${a.id}`}>{a.title || t.label + " Appointment"}</button>
                                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${t.soft} ${t.text} ${t.border}`}><TIcon className="w-3 h-3" /> {t.label}</span>
                                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${s.pill}`}><SIcon className="w-3 h-3" /> {s.label}</span>
                              </div>
                              <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                                <Link to={`/clients/${a.client_id}/appointments`} className="font-medium hover:text-slate-900 hover:underline">{safeClientName(a)}</Link>
                                {a.location && <span>· {a.location}</span>}
                                {a.clinician && <span>· {a.clinician}</span>}
                                {a.escort_staff && <span>· escort {a.escort_staff}</span>}
                                {a.duration_min && <span>· {a.duration_min} min</span>}
                              </div>
                              {a.notes && <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{a.notes}</div>}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <UpcomingPanel
          items={items}
          safety={safety}
          open={upcomingOpen}
          onOpen={setUpcomingOpen}
          onOpenDrawer={(a) => setDrawerAppt(a)}
        />
      </div>

      {/* Appointment detail drawer */}
      <Sheet open={!!drawerAppt} onOpenChange={(v) => !v && setDrawerAppt(null)}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[420px] p-0 flex flex-col" data-testid="appointment-drawer">
          {drawerAppt && (() => {
            const a = drawerAppt;
            const t = typeMeta(a.appointment_type);
            const s = statusMeta(a.status);
            const TIcon = t.Icon; const SIcon = s.Icon;
            const flags = safety?.[a.client_id]?.care_flags || [];
            return (
              <>
                <div className="p-6 pb-3 border-b border-slate-100 shrink-0">
                  <SheetHeader className="space-y-2">
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 self-start ${t.soft} ${t.text} ${t.border}`}>
                      <TIcon className="w-3 h-3" /> {t.label}
                    </div>
                    <SheetTitle className="text-xl">{a.title || t.label + " Appointment"}</SheetTitle>
                    <SheetDescription className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${s.pill}`}>
                        <SIcon className="w-3 h-3" /> {s.label}
                      </span>
                    </SheetDescription>
                  </SheetHeader>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 text-sm">
                  <Row icon={UserRound} label="Service user" value={
                    <Link to={`/clients/${a.client_id}/appointments`} className="text-slate-900 hover:underline font-medium" data-testid="drawer-view-client">{safeClientName(a)}</Link>
                  } />
                  <Row icon={CalendarDays} label="Date" value={new Date(a.scheduled_at).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
                  <Row icon={Clock} label="Time" value={
                    <>
                      {fmtTime(a.scheduled_at)}{a.duration_min ? ` – ${endTime(a.scheduled_at, a.duration_min)}` : ""}
                      {a.duration_min ? <span className="text-xs text-slate-500 ml-2">({a.duration_min} min)</span> : null}
                    </>
                  } />
                  {a.location && <Row icon={MapPin} label="Location" value={a.location} />}
                  {a.clinician && <Row icon={Stethoscope} label="Clinician" value={a.clinician} />}
                  {a.escort_staff && <Row icon={UserRound} label="Escort" value={a.escort_staff} />}
                  {flags.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Risk alerts</div>
                      <CareFlagsPills flags={flags} size="sm" />
                    </div>
                  )}
                  {a.notes && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Notes</div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap border border-slate-200 bg-slate-50 p-2 rounded-sm">{a.notes}</div>
                    </div>
                  )}
                </div>
                <div className="shrink-0 border-t border-slate-100 bg-white p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid="drawer-edit" className="h-9">
                      Edit
                    </Button>
                    <Link to={`/clients/${a.client_id}/appointments`} className="inline-flex" data-testid="drawer-view-user-btn">
                      <Button size="sm" variant="outline" className="h-9 w-full">
                        <ChevRight className="w-3.5 h-3.5 mr-1.5" /> View service user
                      </Button>
                    </Link>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const t = toast.loading("Preparing escort handover pack…");
                      try {
                        await downloadBlob(`/appointments/${a.id}/handover-pdf`,
                          `escort-handover-${(safeClientName(a) || "service-user").replace(/\s+/g, "_")}-${(a.scheduled_at || "").slice(0,10)}.pdf`);
                        toast.success("Escort handover downloaded", { id: t });
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not generate PDF", { id: t });
                      }
                    }}
                    className="w-full h-9 border-sky-300 text-sky-800 hover:bg-sky-50"
                    data-testid="drawer-escort-handover-pdf"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-1.5" /> Escort handover PDF
                  </Button>
                  {a.status !== "completed" && (
                    <Button size="sm" onClick={() => quickStatus(a, "completed", "Appointment marked completed")} className="w-full h-9 bg-green-700 hover:bg-green-800" data-testid="drawer-mark-complete">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark completed
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} className="w-full h-9" data-testid="drawer-reschedule">
                    <Clock className="w-3.5 h-3.5 mr-1.5" /> Reschedule
                  </Button>
                  {a.status !== "cancelled" && (
                    <Button size="sm" variant="outline" onClick={() => quickStatus(a, "cancelled", "Appointment cancelled")} className="w-full h-9 border-red-300 text-red-700" data-testid="drawer-cancel">
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel appointment
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Add / Edit dialog (unchanged behaviour) */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent data-testid="add-appointment-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit appointment" : "New appointment"}</DialogTitle>
            <DialogDescription>{editingId ? "Update, cancel or delete this appointment." : "Create an appointment straight from the calendar."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Service user</Label>
              <Select value={addForm.client_id} onValueChange={(v) => setAddForm({ ...addForm, client_id: v })} disabled={!!editingId}>
                <SelectTrigger data-testid="add-client"><SelectValue placeholder="Pick service user" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Title</Label>
              <Input value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} placeholder="e.g. GP annual review" data-testid="add-title" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={addForm.appointment_type} onValueChange={(v) => setAddForm({ ...addForm, appointment_type: v })}>
                <SelectTrigger data-testid="add-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                <SelectTrigger data-testid="add-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>When</Label>
              <Input type="datetime-local" value={addForm.scheduled_at} onChange={(e) => setAddForm({ ...addForm, scheduled_at: e.target.value })} data-testid="add-when" />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" value={addForm.duration_min} onChange={(e) => setAddForm({ ...addForm, duration_min: e.target.value })} data-testid="add-duration" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={addForm.location} onChange={(e) => setAddForm({ ...addForm, location: e.target.value })} placeholder="e.g. Bridge Surgery" data-testid="add-location" />
            </div>
            <div>
              <Label>Clinician</Label>
              <Input value={addForm.clinician} onChange={(e) => setAddForm({ ...addForm, clinician: e.target.value })} placeholder="e.g. Dr Smith" data-testid="add-clinician" />
            </div>
            <div className="col-span-2">
              <Label>Escort staff</Label>
              <Input value={addForm.escort_staff} onChange={(e) => setAddForm({ ...addForm, escort_staff: e.target.value })} placeholder="e.g. Jane Doe" data-testid="add-escort" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} data-testid="add-notes" />
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {editingId && (
              <>
                <Button variant="outline" onClick={cancelAppt} disabled={saving} data-testid="btn-cancel-appointment" className="border-amber-300 text-amber-800 mr-auto">Mark cancelled</Button>
                <Button variant="outline" onClick={deleteAppt} disabled={saving} data-testid="btn-delete-appointment" className="border-red-300 text-red-700">Delete</Button>
              </>
            )}
            <Button variant="outline" onClick={() => { setAddOpen(false); setEditingId(null); }}>Close</Button>
            <Button onClick={submitAdd} disabled={saving} data-testid="btn-save-appointment" className="bg-slate-900 hover:bg-slate-800">
              {saving ? (editingId ? "Saving…" : "Creating…") : (editingId ? "Save changes" : "Create appointment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800">{value}</div>
      </div>
    </div>
  );
}
